/**
 * Прогноз темпа роста уровня: скорость, серия и даты достижения ступеней.
 *
 * Все функции чистые — вход это журнал пройденных уроков, выход не зависит
 * ни от сети, ни от текущего времени, кроме явно переданного `now`. Так
 * прогноз воспроизводим и проверяется без стора.
 *
 * Скорость считается методом наименьших квадратов по (дни, уровень), а не
 * «первый к последнему»: одиночный провал не должен разворачивать прогноз
 * вниз. Уровень намеренно ограничен теми же границами, что и адаптация
 * (`0.5..4`), — прогноз не может обещать больше потолка модели.
 */

/** Одна запись журнала: когда прошёл урок, каким стал уровень. */
export interface ProgressEntry {
  readonly at: string;
  readonly lessonId: string;
  readonly level: number;
  readonly firstTryCorrect: boolean;
}

/** Ступень владения: подпись и уровень модели, при котором она достигнута. */
export interface CefrTarget {
  readonly label: string;
  readonly level: number;
}

export { LEVEL_MAX, LEVEL_MIN } from '@aptile/shared';
import { LEVEL_MAX, LEVEL_MIN } from '@aptile/shared';

/**
 * Ступени CEFR на шкале модели: старт A1 (1), A2 — 2, B1 — 3, B2 — 4, C1 — 5.
 * Сопоставление линейное и потому читаемое: одна ступень — одна единица.
 */
export const CEFR_TARGETS: readonly CefrTarget[] = [
  { label: 'A2', level: 2 },
  { label: 'B1', level: 3 },
  { label: 'B2', level: 4 },
  { label: 'C1', level: 5 },
];

/** Прогноз достижения одной ступени. */
export interface ForecastProjection {
  readonly target: CefrTarget;
  /** Дней от «сейчас» до ступени; `null`, если темп не положительный. */
  readonly days: number | null;
  /** Дата достижения в ISO; `null` при недостижимом темпе. */
  readonly date: string | null;
  /** Ступень уже взята. */
  readonly reached: boolean;
}

/** Полный прогноз по журналу. */
export interface Forecast {
  /** Прирост уровня в день по прямой регрессии. */
  readonly slopePerDay: number;
  /** Уроков в день за период журнала. */
  readonly lessonsPerDay: number;
  /** Доля уроков, решённых с первой попытки (0..1). */
  readonly accuracy: number;
  /** Дней подряд с хотя бы одним уроком, считая от сегодня/вчера. */
  readonly streakDays: number;
  /** Прогноз по каждой ступени CEFR в её порядке. */
  readonly projections: readonly ForecastProjection[];
}

/** Миллисекунд в сутках — перевод абсолютного времени в дни. */
const MS_PER_DAY = 86_400_000;

/** Доля, ниже которой наклон считается нулевым (защита от деления). */
const SLOPE_EPSILON = 1e-6;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Прирост уровня в день по методу наименьших квадратов.
 * Меньше двух точек — наклон неизвестен, возвращается 0.
 */
export function levelPerDay(log: readonly ProgressEntry[]): number {
  if (log.length < 2) return 0;

  const first = new Date(log[0]?.at ?? 0).getTime();
  const xs = log.map((entry) => (new Date(entry.at).getTime() - first) / MS_PER_DAY);
  const ys = log.map((entry) => entry.level);

  const meanX = xs.reduce((sum, x) => sum + x, 0) / xs.length;
  const meanY = ys.reduce((sum, y) => sum + y, 0) / ys.length;

  let covariance = 0;
  let variance = 0;
  for (let index = 0; index < xs.length; index += 1) {
    const dx = (xs[index] ?? 0) - meanX;
    covariance += dx * ((ys[index] ?? 0) - meanY);
    variance += dx * dx;
  }

  if (variance <= SLOPE_EPSILON) return 0;
  return covariance / variance;
}

/** Локальная дата в виде `YYYY-MM-DD` — ключ дня для серии. */
function dayKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Длина серии: сколько дней подряд (включая сегодня или вчера) есть хотя
 * бы один урок. Если сегодня ещё не занимались, серия не обнуляется —
 * вчерашний день ещё можно «дотянуть».
 */
export function streakDays(
  log: readonly ProgressEntry[],
  now: Date = new Date(),
): number {
  if (log.length === 0) return 0;

  const days = new Set(log.map((entry) => dayKey(new Date(entry.at))));

  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // Не сегодня — начинаем со вчера: серия жива, пока день не пропущен.
  if (!days.has(dayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(dayKey(cursor))) return 0;
  }

  let streak = 0;
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/** Доля уроков с первой попыткой; при пустом журнале — 0. */
function accuracy(log: readonly ProgressEntry[]): number {
  if (log.length === 0) return 0;
  const correct = log.filter((entry) => entry.firstTryCorrect).length;
  return correct / log.length;
}

/** Уроков в день за охваченный период; меньше суток — по числу уроков. */
function lessonsPerDay(log: readonly ProgressEntry[]): number {
  if (log.length === 0) return 0;
  if (log.length === 1) return 1;

  const first = new Date(log[0]?.at ?? 0).getTime();
  const last = new Date(log[log.length - 1]?.at ?? 0).getTime();
  const span = Math.max((last - first) / MS_PER_DAY, 1);
  return log.length / span;
}

/**
 * Считает прогноз по журналу: скорость, серия, точность и даты ступеней.
 * Текущий уровень — последняя запись журнала; ступени ниже берутся как
 * уже достигнутые. При нулевом темпе даты не выдумываются — `null`.
 */
export function forecastLevels(
  log: readonly ProgressEntry[],
  now: Date = new Date(),
): Forecast {
  const ordered = [...log].sort(
    (left, right) => new Date(left.at).getTime() - new Date(right.at).getTime(),
  );

  const slope = levelPerDay(ordered);
  const current = clamp(
    ordered[ordered.length - 1]?.level ?? LEVEL_MIN,
    LEVEL_MIN,
    LEVEL_MAX,
  );

  const projections = CEFR_TARGETS.map((target): ForecastProjection => {
    if (current >= target.level) {
      return { target, days: 0, date: now.toISOString(), reached: true };
    }
    if (slope <= SLOPE_EPSILON) {
      return { target, days: null, date: null, reached: false };
    }

    const days = (target.level - current) / slope;
    const date = new Date(now.getTime() + days * MS_PER_DAY);
    return { target, days, date: date.toISOString(), reached: false };
  });

  return {
    slopePerDay: slope,
    lessonsPerDay: lessonsPerDay(ordered),
    accuracy: accuracy(ordered),
    streakDays: streakDays(ordered, now),
    projections,
  };
}
